/**
 * Health History Hook
 *
 * React Query hook for fetching system health snapshot history.
 * Wraps GET /api/v1/admin/health/history?period=... with typed params and response.
 */

import { useQuery } from "@tanstack/react-query";

import { apiRequest } from "../api/client";

// ============================================================================
// Types
// ============================================================================

/** Valid history periods */
export type HealthHistoryPeriod = "24h" | "7d";

/** Single health snapshot from the API */
export interface HealthSnapshotItem {
  readonly id: string;
  readonly status: string;
  readonly d1LatencyMs: number | null;
  readonly kvLatencyMs: number | null;
  readonly r2LatencyMs: number | null;
  readonly createdAt: string;
}

/** Response payload from the health history endpoint (inner data object) */
interface HealthHistoryData {
  readonly snapshots: readonly HealthSnapshotItem[];
  readonly period: HealthHistoryPeriod;
  readonly from: string;
  readonly to: string;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Fetch health snapshot history for the given period.
 *
 * Uses React Query for caching and loading states.
 * Requires admin:health capability (enforced server-side).
 */
export function useHealthHistory(period: HealthHistoryPeriod): {
  snapshots: readonly HealthSnapshotItem[];
  from: string | null;
  to: string | null;
  isLoading: boolean;
  isError: boolean;
  error: string | null;
  refetch: () => Promise<unknown>;
} {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["health-history", period],
    queryFn: async (): Promise<HealthHistoryData> => {
      const response = await apiRequest<HealthHistoryData>(
        `/api/v1/admin/health/history?period=${period}`
      );

      if (!response.success) {
        throw new Error(response.error.message);
      }

      return response.data;
    },
  });

  return {
    snapshots: data?.snapshots ?? [],
    from: data?.from ?? null,
    to: data?.to ?? null,
    isLoading,
    isError,
    error: error instanceof Error ? error.message : null,
    refetch,
  };
}
